import { FC, MouseEvent, useState } from 'react';

import Box from '@mui/material/Box/Box';

import NorthEastIcon from '@mui/icons-material/NorthEast';

import {
  ContactFormWrapper,
  ContactFormText,
  WebWrapper,
  ContactFormDescription,
  ContactFormButtonsWrapper,
  ContactFormFooterLink,
  ContactFormFooterLinkMobileBreak,
  InformationField,
} from './ContactForm.style';
import { useIsMobile } from 'hooks/useIsMobile';

import { colors } from 'styles';

import { useContactFormData } from 'hooks/useContactFormData';

import TopWeb from 'assets/contact-form/top-web.webp';
import BottomWeb from 'assets/contact-form/button-web.webp';

import { ContactFormType } from 'types/contact-us';
import { policyMeta } from 'constants/pages';
import { Button } from 'components/Buttons';
import { StarterPackModal } from 'components/StarterPackModal';
import { useNavigate } from 'react-router-dom';

interface ContactFormProps {
  onClick: () => void;
  isShowTermsAndConditions?: boolean;
  isCTABlock?: boolean;
}

export const ContactForm: FC<ContactFormProps> = ({
  onClick,
  isShowTermsAndConditions = false,
  isCTABlock = false,
}) => {
  const {
    title,
    description,
    additionalText,
    buttonLabel,
    type,
    additionalButton,
    additionalButtonLink,
    buttonLink,
    padding,
    footerLink,
  } = useContactFormData(isCTABlock);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isStarterPackOpen, setIsStarterPackOpen] = useState(false);

  const isLandingCta =
    type === ContactFormType.LANDING_IST ||
    type === ContactFormType.LANDING_COUNTER_DRONE ||
    type === ContactFormType.LANDING_AIRPORT_GROUND_OPS ||
    type === ContactFormType.LANDING_INTERCEPTOR_USV;

  // On landing pages the primary CTA opens the same Starter Pack application form
  // as the hero's "Get Started" button instead of the generic contact modal.
  const handlePrimaryClick = isLandingCta ? () => setIsStarterPackOpen(true) : onClick;

  const handleFooterLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (footerLink?.href.startsWith('/')) {
      event.preventDefault();
      navigate(footerLink.href);
    }
  };
  const buttonWidth = isMobile
    ? 'full'
    : type === ContactFormType.SENSORS
    ? 323
    : isLandingCta
    ? 280
    : 240;

  return (
    <ContactFormWrapper id="contact-us" mt="auto" isCTABlock={isCTABlock} padding={padding}>
      <WebWrapper left="0" top="0">
        <img src={TopWeb} alt="top web" />
      </WebWrapper>

      <WebWrapper bottom="-30%" right="0">
        <img src={BottomWeb} alt="bottom web" />
      </WebWrapper>

      <ContactFormText isCTABlock={isCTABlock || !!description}>{title}</ContactFormText>

      {description && <ContactFormDescription>{description}</ContactFormDescription>}

      <Box marginTop={description ? '32px' : '48px'} width="100%">
        <ContactFormButtonsWrapper naturalOrder={isLandingCta}>
          {buttonLink === '' ? (
            <Button
              btnType={!isCTABlock ? 'tertiary' : 'primary'}
              onClick={handlePrimaryClick}
              aria-label={buttonLabel}
              width={buttonWidth}
            >
              {buttonLabel}
            </Button>
          ) : buttonLink.startsWith('/') ? (
            <Button
              width={buttonWidth}
              btnType="secondary"
              onClick={() => navigate(buttonLink)}
            >
              {buttonLabel}
            </Button>
          ) : (
            <a href={buttonLink} target="_blank" rel="noreferrer">
              <Button
                width={buttonWidth}
                btnType="tertiary"
              >
                {buttonLabel}
              </Button>
            </a>
          )}

          {type !== ContactFormType.SENSORS &&
            additionalButton &&
            !(type === ContactFormType.RESOURCES && isCTABlock) &&
            (additionalButtonLink.startsWith('http') ? (
              <a href={additionalButtonLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                <Button
                  btnType="secondary"
                  key={additionalButton}
                  width={buttonWidth}
                  aria-label={additionalButton}
                >
                  {additionalButton}
                </Button>
              </a>
            ) : (
              <Button
                btnType={type === ContactFormType.DEFAULT ? 'tertiary' : 'secondary'}
                key={additionalButton}
                width={buttonWidth}
                onClick={() => {
                  if (additionalButtonLink) {
                    navigate(additionalButtonLink);
                  } else {
                    onClick();
                  }
                }}
                aria-label={additionalButton}
              >
                {additionalButton}
              </Button>
            ))}

          {type === ContactFormType.SENSORS && additionalButton && (
            <Button
              btnType="secondary"
              key={additionalButton}
              width={buttonWidth}
              onClick={() => {
                if (additionalButtonLink) {
                  navigate(additionalButtonLink);
                } else {
                  onClick();
                }
              }}
              aria-label={additionalButton}
            >
              {additionalButton}
            </Button>
          )}
        </ContactFormButtonsWrapper>
      </Box>

      {footerLink && (
        <ContactFormFooterLink href={footerLink.href} onClick={handleFooterLinkClick}>
          <span>
            {footerLink.prefix}
            {footerLink.midfix && <ContactFormFooterLinkMobileBreak />}
            {footerLink.midfix}
            <strong>{footerLink.bold}</strong>
            {footerLink.suffix}
          </span>
          <NorthEastIcon />
        </ContactFormFooterLink>
      )}

      {type === ContactFormType.RESOURCES && additionalText && (
        <Box marginTop="32px">
          <ContactFormDescription
            sx={{
              marginTop: '0',
            }}
          >
            {additionalText}
          </ContactFormDescription>
        </Box>
      )}

      {isShowTermsAndConditions && (
        <Box marginTop="32px" padding="0 24px">
          <InformationField>
            By clicking Subscribe you&apos;re confirming that you agree with our{' '}
            <a
              href={policyMeta.url}
              target="_blank"
              rel="noreferrer"
              style={{
                color: colors.white,
              }}
            >
              Terms and Conditions.
            </a>
          </InformationField>
        </Box>
      )}

      <StarterPackModal
        isOpen={isStarterPackOpen}
        onClose={() => setIsStarterPackOpen(false)}
      />
    </ContactFormWrapper>
  );
};
